import { Avatar, Text } from '@mantine/core'
import type { MantineSize } from '@mantine/core'

/**
 * A person's face, or their initials when they have not set one.
 *
 * One component so every place showing a person looks the same and falls back
 * the same way. The picture is fetched through the API like any other
 * protected resource, so the access check applies to it too - and a 404 for
 * somebody without one is the ordinary case, which is why the fallback is the
 * initials rather than an error.
 */
export function UserAvatar({
  userId,
  initials,
  hasAvatar = false,
  size = 32,
  /**
   * Anything that changes when the picture does - the account's updated_at
   * will do. Without it a newly uploaded picture stays hidden behind the
   * response's own cache lifetime.
   */
  version,
  radius = 'xl',
}: {
  userId: string
  initials: string
  hasAvatar?: boolean
  size?: number | MantineSize
  version?: string | null
  radius?: MantineSize | number
}) {
  const fontSize = typeof size === 'number' ? Math.max(9, Math.round(size / 2.8)) : 12

  return (
    <Avatar
      size={size}
      radius={radius}
      color="indigo"
      src={hasAvatar ? avatarUrl(userId, version) : null}
      alt={initials}
      styles={{
        root: {
          // Pictures are saved on a white backdrop, so against a white header
          // or card they have no edge at all and read as a floating head. The
          // ring is only for pictures: initials sit on a coloured circle that
          // already has one.
          //
          // Drawn outside the box rather than inset. An inset shadow paints
          // beneath child content, and the image fills the avatar completely,
          // so it covered the ring entirely - present in the CSS and invisible
          // on screen. Outside it also costs no pixel of the image, which a
          // border would.
          boxShadow: hasAvatar ? '0 0 0 1px var(--mantine-color-default-border)' : undefined,
        },
      }}
    >
      <Text size={`${fontSize}px`} fw={600}>
        {initials}
      </Text>
    </Avatar>
  )
}

/** Same origin, so the session cookie travels with it. */
export function avatarUrl(userId: string, version?: string | null): string {
  const query = version ? `?v=${encodeURIComponent(version)}` : ''

  return `/api/v1/user/${userId}/avatar${query}`
}
